import type { CreatorSiteProfileSeedSource } from './creator-site-profile-seed';

export type CreatorSiteAiCopyDraft = {
  generatedAt: string;
  fields: Record<string, string>;
};

export type CreatorSiteAiCopySectionType = 'hero' | 'about' | 'rich_text';

const AI_COPY_SECTION_TYPES = new Set<string>(['hero', 'about', 'rich_text']);

export function isCreatorSiteAiCopySectionType(value: string): value is CreatorSiteAiCopySectionType {
  return AI_COPY_SECTION_TYPES.has(value);
}

export function generateCreatorSiteSectionCopyDraft(
  sectionType: CreatorSiteAiCopySectionType,
  profile: CreatorSiteProfileSeedSource | null,
  siteTitle?: string | null,
): CreatorSiteAiCopyDraft {
  const name = profile?.displayName?.trim() || profile?.username || 'your brand';
  const headline = profile?.headline?.trim() || '';
  const bio = profile?.bio?.trim() || '';
  const topics = Array.isArray(profile?.topics) ? profile.topics : [];
  const topic = (typeof topics[0] === 'string' ? topics[0].trim() : '') || 'your niche';
  const title = siteTitle?.trim() || name;

  const generatedAt = new Date().toISOString();

  if (sectionType === 'hero') {
    return {
      generatedAt,
      fields: {
        headline: title,
        subheadline: headline || `Helping you grow in ${topic}.`,
        ctaLabel: 'Get started',
        ctaHref: '#contact',
      },
    };
  }

  if (sectionType === 'about') {
    return {
      generatedAt,
      fields: {
        headline: `About ${name}`,
        body:
          bio ||
          `${name} helps audiences in ${topic} with practical guidance and proven frameworks. Update this draft with your story, credentials, and what makes your approach unique.`,
      },
    };
  }

  return {
    generatedAt,
    fields: {
      body:
        bio ||
        `Share more about your work, offers, and how visitors can take the next step with ${name}.`,
    },
  };
}

export function applyCreatorSiteAiCopyDraft(
  content: Record<string, unknown>,
  draft: CreatorSiteAiCopyDraft,
): Record<string, unknown> {
  const next = { ...content, ...draft.fields };
  delete next._aiDraft;
  return next;
}
